"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const destinations = [
{
  id: "canada",
  flag: "🇨🇦",
  country: "Canada",
  tagline: "World-class education, post-study work rights",
  image: "https://images.unsplash.com/photo-1541308383720-fa208853c143",
  imageAlt: "Toronto skyline with CN Tower reflecting on Lake Ontario, Canada",
  stats: [
  { val: "50+", label: "Universities" },
  { val: "3 Yrs", label: "Post-Study Visa" },
  { val: "Top 10", label: "Global Rankings" }],
  highlights: ["Engineering", "Business", "Health Sciences", "IT"],
  color: "#8B1A1A",
  accent: "#B02020",
  popular: ["University of Toronto", "UBC", "McGill University", "University of Waterloo"]
},
{
  id: "australia",
  flag: "🇦🇺",
  country: "Australia",
  tagline: "World-class education, post-study work rights",
  image: "/assets/images/australia-destination.png",
  imageAlt: "Sydney Opera House and Harbour Bridge at golden hour with vibrant blue harbour waters, iconic Australian landmarks",
  stats: [
  { val: "42+", label: "Universities" },
  { val: "4 Yrs", label: "Post-Study Visa" },
  { val: "Top 5", label: "Global Rankings" }],
  highlights: ["Engineering", "Nursing", "Business", "Education"],
  color: "#0A2D6E",
  accent: "#1E4FA8",
  popular: ["University of Melbourne", "Monash University", "UNSW Sydney", "ANU"]
},
{
  id: "uk",
  flag: "🇬🇧",
  country: "United Kingdom",
  tagline: "Prestigious degrees, rich academic heritage",
  image: "https://images.unsplash.com/photo-1641394175981-886880d3d8d4",
  imageAlt: "Tower Bridge and Thames River in London, England at golden hour",
  stats: [
  { val: "38+", label: "Universities" },
  { val: "2 Yrs", label: "Graduate Visa" },
  { val: "4 of Top 10", label: "Global Rankings" }],
  highlights: ["Law", "Finance", "STEM", "Humanities"],
  color: "#8B1A1A",
  accent: "#B02020",
  popular: ["University of Edinburgh", "King's College London", "University of Manchester", "UCL"]
},
{
  id: "germany",
  flag: "🇩🇪",
  country: "Germany",
  tagline: "Affordable tuition, world-leading engineering programs",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1160f1fdb-1772815728986.png",
  imageAlt: "Beautiful German university campus with historic architecture and green courtyards",
  stats: [
  { val: "60+", label: "Universities" },
  { val: "€0–5K", label: "Annual Tuition" },
  { val: "Top 50", label: "Global Rankings" }],
  highlights: ["Engineering", "Computer Science", "MBA", "Research"],
  color: "#1A4A1A",
  accent: "#2A6A2A",
  popular: ["TU Munich", "RWTH Aachen", "University of Stuttgart", "Heidelberg University"]
},
{
  id: "ireland",
  flag: "🇮🇪",
  country: "Ireland",
  tagline: "English-speaking Europe with strong tech industry ties",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15be0a920-1773138579134.png",
  imageAlt: "Trinity College Dublin historic library and campus, Ireland",
  stats: [
  { val: "20+", label: "Universities" },
  { val: "2 Yrs", label: "Stay-Back Visa" },
  { val: "Top 100", label: "Global Rankings" }],
  highlights: ["Business", "Pharmacy", "Technology", "Finance"],
  color: "#1A4A1A",
  accent: "#2A6A2A",
  popular: ["Trinity College Dublin", "University College Dublin", "NUI Galway", "DCU"]
},
{
  id: "newzealand",
  flag: "🇳🇿",
  country: "New Zealand",
  tagline: "Safe, scenic, and globally respected qualifications",
  image: "/assets/images/newzealand-destination.png",
  imageAlt: "Queenstown New Zealand with the Remarkables mountain range reflected in Lake Wakatipu, stunning natural scenery",
  stats: [
  { val: "8+", label: "Universities" },
  { val: "3 Yrs", label: "Post-Study Visa" },
  { val: "Top 200", label: "Global Rankings" }],
  highlights: ["Agriculture", "Tourism", "Engineering", "Health"],
  color: "#0A2D6E",
  accent: "#1E4FA8",
  popular: ["University of Auckland", "Victoria University", "University of Otago", "AUT"]
}];


export default function DestinationsSection() {
  const [active, setActive] = useState("canada");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    ref.current?.querySelectorAll(".reveal, .reveal-scale").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const current = destinations.find((d) => d.id === active)!;

  return (
    <section id="destinations" ref={ref} className="py-28 px-6 lg:px-10 dot-pattern">
      <div className="max-w-site mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="reveal flex justify-center mb-4">
            <span className="section-label">Where Our Students Go</span>
          </div>
          <h2
            className="font-serif reveal delay-1 mb-4"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            
            The World's Best Campuses Are{" "}
            <span className="gradient-text italic">Closer Than You Think.</span>
          </h2>
          <p className="text-ink-muted text-lg max-w-xl mx-auto reveal delay-2" style={{ fontWeight: 400 }}>
            We have deep expertise and established university relationships across the globe's most sought-after study destinations.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 reveal delay-2">
          {destinations.map((d) =>
          <button
            key={d.id}
            onClick={() => setActive(d.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
            active === d.id ?
            "bg-crimson text-white shadow-crimson-sm" :
            "bg-white text-ink-muted border border-crimson-100 hover:border-crimson/30 hover:text-crimson"}`
            }>
            
              <span>{d.flag}</span>
              {d.country}
            </button>
          )}
        </div>

        {/* Main card */}
        <div
          key={active}
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-[24px] overflow-hidden shadow-soft-lg reveal-scale delay-1"
          style={{ animation: "scaleIn 0.5s ease-out both" }}>
          
          {/* Image side */}
          <div className="relative h-64 lg:h-auto min-h-[360px] overflow-hidden">
            <AppImage
              src={current.image}
              alt={current.imageAlt}
              fill
              className="object-cover transition-transform duration-700 hover:scale-105"
              sizes="(max-width: 1024px) 100vw, 50vw" />
            
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${current.color}80 0%, transparent 60%)` }} />
            
            {/* Country badge */}
            <div className="absolute top-6 left-6 glass-card rounded-[12px] px-4 py-2.5 flex items-center gap-3">
              <span className="text-3xl">{current.flag}</span>
              <div>
                <p className="font-serif font-bold text-ink text-lg">{current.country}</p>
                <p className="text-xs text-ink-muted">{current.tagline}</p>
              </div>
            </div>
            {/* Stats overlay */}
            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-2">
              {current.stats.map((s) =>
              <div key={s.label} className="glass-card rounded-[10px] p-3 text-center">
                  <p className="font-serif font-bold text-crimson text-lg">{s.val}</p>
                  <p className="text-xs text-ink-muted font-medium">{s.label}</p>
                </div>
              )}
            </div>
          </div>

          {/* Content side */}
          <div className="bg-white p-8 lg:p-10 flex flex-col justify-between">
            <div>
              <h3
                className="font-serif text-2xl font-bold text-ink mb-2"
                style={{ letterSpacing: "-0.01em" }}>
                
                Study in {current.country}
              </h3>
              <p className="text-ink-muted mb-6">{current.tagline}</p>

              {/* Popular Streams */}
              <div className="mb-6">
                <p className="text-xs font-bold text-ink-muted uppercase tracking-widest mb-3">Popular Streams</p>
                <div className="grid grid-cols-1 gap-3">
                  {current.highlights.map((h) =>
                  <div key={h} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-crimson/10 flex items-center justify-center flex-shrink-0">
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4l2.5 2.5L9 1" stroke="#8B1A1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span className="text-sm text-ink font-medium">{h}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Popular universities */}
              <div>
                <p className="text-xs font-bold text-ink-muted uppercase tracking-widest mb-3">Popular Universities</p>
                <div className="flex flex-wrap gap-2">
                  {current.popular.map((u) =>
                  <span
                    key={u}
                    className="text-xs bg-parchment border border-crimson-100 text-ink px-3 py-1.5 rounded-full font-medium">
                    
                      {u}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-crimson-50">
              <a href="#contact" className="btn-primary w-full text-center block">
                Find the Right Country for Your Profile — Free Assessment
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}